import React, { useMemo } from 'react';
import {
  Container,
  Form,
  Button,
  toaster,
  Message,
  Loader,
  Placeholder
} from 'rsuite';
import {
  IconButton,
  Tooltip
} from "@mui/material"
import { AddOutline } from '@rsuite/icons';
import {
    Edit,
    Delete,
    Cancel,
    Update
} from "@mui/icons-material"
import ConfirmationAlert from '../components/ConfirmationAlert'
import CLoader from "../components/Loader"

import {
  useCreateClassMutation,
  useLazyGetClassStudentsListQuery,
  useLazyGetInstitutionClassesQuery,
  useEditInstitutionClassMutation,
  useDeleteInstitutionClassMutation
} from '../Redux/actions/classSetup.action'
import {
  useLazyGetClassStudentsQuery
} from "../Redux/actions/studentAction"

import { GetCurrentUser } from '../utils/hooks';

function CreateClass() {
  const [className, setClassName] = React.useState('');
  const [selectedClass, setSelectedClass] = React.useState(null);
  const [editClassId, setEditClassId] = React.useState(null);
  const [editClassName, setEditClassName] = React.useState('');
  const [createClass, createState] = useCreateClassMutation();
  const [isEdit, setIsEdit] = React.useState(false)
  const [selectedClassId, setSelectedClassid] = React.useState('')
  const [isDelete, setIsDelete] = React.useState(false)

  const [editClass, editClassState] = useEditInstitutionClassMutation()
  const [deleteInstitutionClass] = useDeleteInstitutionClassMutation()
  const [getClassStudentsList, getClassStudentsListState] = useLazyGetClassStudentsListQuery()
  const [getStudentsApi,getStudentApiStatus] = useLazyGetClassStudentsQuery()

  const [
    getInstitutionClasses,
    getInstitutionClassesState
  ] = useLazyGetInstitutionClassesQuery()

  const studentList = useMemo(()=> {
    if(getStudentApiStatus.isSuccess){
      return getStudentApiStatus?.currentData?.list
    }else{
      return []
    }
  },[getStudentApiStatus.status])


  const fetchInstitutionClasses = React.useCallback(() => {
    const user = GetCurrentUser();

    if (user?.meta_data?.user_platform) {
      getInstitutionClasses({
        institution_ref: user.meta_data.user_platform
      })
    }
  }, [getInstitutionClasses])

  const handleClassClick = (classItem) => {
    setSelectedClass(classItem)

    const user = GetCurrentUser()

    if (user?.meta_data?.user_platform) {
      getClassStudentsList({
        institution_ref: user.meta_data.user_platform,
        class_id: classItem.id
      })
      getStudentsApi({
        school_id:user.meta_data.user_platform,
        class_id:classItem.id
      })
    }
  }

  const handleSubmit = async () => {
    if (!className.trim()) {
      toaster.push(
        <Message type="warning">
          Please enter a class name
        </Message>,
        { placement: 'topCenter' }
      )
      return
    }

    const user = GetCurrentUser();

    if (user?.meta_data?.user_platform) {
      let isClassExist = classes.find((classItem) => classItem.class_name?.toLocaleLowerCase() === className.toLocaleLowerCase());
      if (isClassExist) {
        toaster.push(
          <Message type="warning">
            Class already exists
          </Message>,
          { placement: 'topCenter' }
        )
        return
      }

      try {
        await createClass({
          class_name: className,
          institution_ref: user.meta_data.user_platform,
          meta_data: {}
        })

        setClassName('')
      } catch (error) {
        console.log(error)
      }
    }
  }

  React.useEffect(() => {
    fetchInstitutionClasses()
  }, [fetchInstitutionClasses])

  React.useEffect(() => {
    if (createState.isSuccess) {
      toaster.push(
        <Message type="success">
          Class created successfully
        </Message>,
        { placement: 'topCenter' }
      )

      fetchInstitutionClasses()
    }

    if (createState.isError) {
      toaster.push(
        <Message type="error">
          {createState?.error?.data?.detail ||
            'Failed to create class'}
        </Message>,
        { placement: 'topCenter' }
      )
    }
  }, [createState, fetchInstitutionClasses])

  function handleEditClick(classItem) {
    setEditClassId(classItem.id);
    setEditClassName(classItem.class_name)
  }

  function handleEditCancel() {
    setEditClassId(null)
    setEditClassName('')
    setIsEdit(false)
    setClassName("")
    setSelectedClassid(null)
  }

  async function handleEditSave() {
    const user = GetCurrentUser();
    if (!user?.meta_data?.user_platform || !editClassId) return;
    try {
      await editClass({
        class_id: editClassId,
        class_name: className,
        institution_ref: user.meta_data.user_platform,
        meta_data: {}
      });
      // Reset edit state
      setEditClassId(null)
      setEditClassName('')
      setClassName("")
      setIsEdit(false)
      // Refresh classes list
      fetchInstitutionClasses()
      toaster.push(
        <Message type="success">
          Class updated successfully
        </Message>,
        { placement: 'topCenter' }
      )
    } catch (error) {
      toaster.push(
        <Message type="error">
          {error?.data?.detail || 'Failed to update class'}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  }

  async function handleDelete() {
    const user = GetCurrentUser()
    if (!user?.meta_data?.user_platform && selectedClassId) return;
    try {
      await deleteInstitutionClass({ class_id: selectedClassId, institution_ref: user.meta_data.user_platform });
      fetchInstitutionClasses();
      setIsDelete(false)
      setSelectedClassid("")
      toaster.push(
        <Message type="success">
          Class deleted successfully
        </Message>,
        { placement: 'topCenter' }
      )
    } catch (error) {
      console.error(error);
      toaster.push(
        <Message type="error">
          {error?.data?.detail || 'Failed to delete class'}
        </Message>,
        { placement: 'topCenter' }
      )
    }
  }

  const classes =
    getInstitutionClassesState?.currentData?.list ||
    getInstitutionClassesState?.data?.list ||
    [];

  const students =
    getClassStudentsListState?.currentData?.list ||
    getClassStudentsListState?.data?.list ||
    []

  return (
    <>
      <style>
        {`
                .class-page {
                    min-height: 100vh;
                    background: #f8fafc;
                    padding: 24px;
                }

                .page-header {
                    margin-bottom: 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    width: 100%;
                    justify-content: center;
                    align-items: center;
                }

                .page-title {
                    font-size: 30px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    margin-top: 6px;
                }

                .dashboard-layout {
                    display: grid;
                    grid-template-columns: 380px 1fr;
                    gap: 24px;
                }

                .panel-card {
                    background: #fff;
                    border-radius: 18px;
                    padding: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0,0,0,.04);
                }

                .left-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 16px;
                }

                .class-list {
                    max-height: 600px;
                    overflow-y: auto;
                }

                .class-item {
                    padding: 14px 16px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    cursor: pointer;
                    margin-bottom: 12px;
                    transition: all .2s ease;
                    background: #fff;
                }

                .class-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,.08);
                }

                .class-item.active {
                    background: #eef2ff;
                    border-color: #4f46e5;
                }

                .class-name {
                    font-size: 15px;
                    font-weight: 600;
                    color: #0f172a;
                }

                .class-meta {
                    font-size: 12px;
                    color: #64748b;
                    margin-top: 4px;
                }

                .students-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .students-count {
                    background: #eef2ff;
                    color: #4f46e5;
                    padding: 6px 12px;
                    border-radius: 999px;
                    font-size: 13px;
                    font-weight: 600;
                }

                .students-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 16px;
                }

                .student-card {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px;
                    border-radius: 14px;
                    border: 1px solid #e2e8f0;
                    transition: all .2s ease;
                }

                .student-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,.06);
                }

                .student-avatar {
                    width: 52px;
                    height: 52px;
                    border-radius: 50%;
                    background: linear-gradient(
                        135deg,
                        #4f46e5,
                        #7c3aed
                    );
                    color: #fff;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-weight: 700;
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .student-name {
                    font-weight: 600;
                    color: #0f172a;
                }

                .student-detail {
                    font-size: 13px;
                    color: #64748b;
                    margin-top: 3px;
                }

                .empty-state {
                    min-height: 450px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                    color: #94a3b8;
                    text-align: center;
                }

                .empty-state h3 {
                    margin-top: 16px;
                    color: #475569;
                }

                @media (max-width: 992px) {
                    .dashboard-layout {
                        grid-template-columns: 1fr;
                    }
                }
            `}
      </style>

      <Container className="class-page">
        <div className="page-header">
          <h1 className="page-title">Class Management</h1>
          <p className="page-subtitle">
            Create classes and manage students efficiently.
          </p>
        </div>

        <div className="dashboard-layout">
          {/* Left Panel */}
          <div className="left-panel">
            <div className="panel-card">
              <div className="section-title">
                {isEdit ? "Update Class Name" : "Create New Class"}
              </div>

              <Form fluid>
                <Form.Group>
                  <Form.Control
                    value={className}
                    placeholder="Enter class name"
                    onChange={setClassName}
                  />
                </Form.Group>

                <Button
                  appearance="primary"
                  block
                  loading={createState.isLoading}
                  startIcon={isEdit ? <Update /> : <AddOutline />}
                  onClick={isEdit ? handleEditSave : handleSubmit}
                >
                  {isEdit ? "Update Class" : "Create Class"}
                </Button>
                {
                  isEdit && (
                    <Button
                      appearance="subtle"
                      block
                      loading={createState.isLoading}
                      startIcon={<Cancel/>}
                      onClick={handleEditCancel}
                    >
                      Cancel
                    </Button>
                  )
                }
              </Form>
            </div>

            <div className="panel-card">
              <div className="section-title">
                Classes
              </div>

              <div className="class-list">
                {getInstitutionClassesState.isLoading ? (
                  <Placeholder.Paragraph rows={6} />
                ) : (
                  Array.isArray(classes) && classes?.map((item) => (
                    <div
                      key={item.id}
                      className={`class-item ${selectedClass?.id === item.id ? 'active' : ''}`}
                      onClick={() => handleClassClick(item)}
                      style={{
                        maxHeight:"50vh"
                      }}
                    >
                      <div style={{
                        display: "flex",
                        flexDirection: "column"
                      }}>
                        <div className="class-name">
                          {item.class_name}
                        </div>
                        <div className="class-meta">
                          Class ID: {item.id}
                        </div>
                      </div>
                      <div style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "4px"
                      }}>
                        <Tooltip title={"Edit?"} arrow placement='bottom'>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(item);
                              setIsDelete(false)
                              setIsEdit(true)
                              setSelectedClassid(item.id)
                              setClassName(item?.class_name)
                            }}
                            style={{ marginTop: 4 }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"Delete?"} arrow placement='bottom'>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsEdit(false);
                              setIsDelete(true)
                              setSelectedClassid(item.id)
                              // handleDelete(item.id); 
                            }}
                            style={{ marginTop: 4 }}
                          >
                            <Delete color='red' />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="panel-card">
            {!selectedClass ? (
              <div className="empty-state">
                <h3>Select a Class</h3>
                <p>
                  Choose a class from the left panel to view
                  students.
                </p>
              </div>
            ) : (
              <>
                <div className="students-header">
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        color: '#0f172a'
                      }}
                    >
                      {selectedClass.class_name}
                    </h2>
                  </div>

                  <div className="students-count">
                    {studentList?.length || 0} Students
                  </div>
                </div>

                {getStudentApiStatus.isFetching ? (
                  <div
                    style={{
                      padding: 60,
                      textAlign: 'center'
                    }}
                  >
                    <Loader size="md" content="Loading Students..." />
                  </div>
                ) : (
                  <div className="students-grid" style={{
                    maxHeight:"100vh",
                    overflowY:"scroll"
                  }}>
                    {Array.isArray(studentList) && studentList.length > 0 && studentList?.map((student) => (
                      <div
                        className="student-card"
                        key={student.id}
                      >
                        <div className="student-avatar">
                          {student.name
                            ?.charAt(0)
                            ?.toUpperCase() || 'S'}
                        </div>

                        <div>
                          <div className="student-name">
                            {student.name}
                          </div>

                          <div className="student-detail">
                            Roll No:{' '}
                            {student.roll_no ||
                              'N/A'}
                          </div>

                          <div className="student-detail">
                            ID: {student.id}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <ConfirmationAlert
          open={isDelete}
          onConfirm={handleDelete}
          onCancel={() => {
            setIsDelete(false);
            setSelectedClassid(null)
          }}
          title="Are You Sure You Want to Delete?"
        />
        {
          (
            getStudentApiStatus.isLoading
          ) && (
            <CLoader  
              show={true}
            />
          )
        }
      </Container>
    </>
  )
}

export default CreateClass;